import React from "react";
import { Smartphone, Tablet, Globe, Apple } from "lucide-react";

/**
 * Shared digital app advertising config form.
 * Used inside Package modal + Sponsor modal.
 *
 * Props:
 *  - value: DigitalAdsConfig object (or undefined)
 *  - onChange: (next) => void
 *  - testIdPrefix: string
 */
export const PLACEMENTS = [
    { key: "home_banner",          label: "Home page banner",        sub: "Top strip on the app's home" },
    { key: "home_rectangle",       label: "Home page rectangle",     sub: "Mid-feed rectangle on home" },
    { key: "internal_banner",      label: "Internal pages banner",   sub: "Persistent banner on inner screens" },
    { key: "internal_interstitial",label: "Internal interstitial",   sub: "Full-screen takeover between pages" },
];

export const PLATFORMS = [
    { key: "iphone",  label: "iPhone",  Icon: Apple },
    { key: "android", label: "Android", Icon: Smartphone },
    { key: "ipad",    label: "iPad",    Icon: Tablet },
    { key: "pwa",     label: "PWA",     Icon: Globe },
];

const RULE_TYPES = [
    { key: "none",        label: "Always on" },
    { key: "period",      label: "Period of validity" },
    { key: "impressions", label: "Impressions cap" },
    { key: "clicks",      label: "Clicks cap" },
];

const emptyAds = () => ({
    enabled: false, placements: [], platforms: [], rule_type: "none",
    period_start: "", period_end: "", impressions_cap: null, clicks_cap: null, notes: "",
});

export default function DigitalAdsForm({ value, onChange, testIdPrefix = "ads", inheritedFromPackage = null, allowInherit = false }) {
    const ads = value || emptyAds();
    const set = (patch) => onChange({ ...ads, ...patch });
    const toggleArr = (key, item) => {
        const cur = ads[key] || [];
        const next = cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item];
        set({ [key]: next });
    };

    return (
        <div className="border hairline" data-testid={`${testIdPrefix}-block`}>
            {/* Header / enable toggle */}
            <div className="flex items-center justify-between px-4 py-3 border-b hairline">
                <div className="flex items-center gap-3">
                    <Smartphone size={14} className="text-[var(--sb-amber)]" />
                    <div>
                        <div className="text-sm font-display font-bold tracking-tight">Digital app advertising</div>
                        <div className="text-[11px] text-zinc-500">Bundle fan-app placements with this {testIdPrefix.includes("sponsor") ? "sponsor" : "package"}.</div>
                    </div>
                </div>
                {allowInherit ? (
                    <div className="flex items-center gap-2 text-[11px] text-zinc-400">
                        <span>Override package?</span>
                        <button type="button" onClick={() => set({ enabled: !ads.enabled })}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${ads.enabled ? "bg-[var(--sb-amber)]" : "bg-white/[0.08]"}`}
                            data-testid={`${testIdPrefix}-toggle`}>
                            <span className={`inline-block h-3.5 w-3.5 transform bg-white transition-transform ${ads.enabled ? "translate-x-5" : "translate-x-1"}`} />
                        </button>
                    </div>
                ) : (
                    <button type="button" onClick={() => set({ enabled: !ads.enabled })}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${ads.enabled ? "bg-[var(--sb-amber)]" : "bg-white/[0.08]"}`}
                        data-testid={`${testIdPrefix}-toggle`}>
                        <span className={`inline-block h-3.5 w-3.5 transform bg-white transition-transform ${ads.enabled ? "translate-x-5" : "translate-x-1"}`} />
                    </button>
                )}
            </div>

            {/* Inherited preview when override disabled */}
            {allowInherit && !ads.enabled && inheritedFromPackage && inheritedFromPackage.enabled && (
                <div className="px-4 py-3 text-[11px] text-zinc-400 bg-white/[0.02]">
                    Inheriting from package: {(inheritedFromPackage.placements || []).length} placement(s) · {(inheritedFromPackage.platforms || []).join(", ") || "no platforms set"} · {inheritedFromPackage.rule_type === "none" ? "always on" : inheritedFromPackage.rule_type}
                </div>
            )}

            {ads.enabled && (
                <div className="p-4 space-y-5">
                    {/* Placements */}
                    <div>
                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-2">Placements</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {PLACEMENTS.map((p) => {
                                const on = (ads.placements || []).includes(p.key);
                                return (
                                    <button key={p.key} type="button" onClick={() => toggleArr("placements", p.key)}
                                        className={`text-left border px-3 py-2.5 transition-colors ${on ? "border-[var(--sb-amber)] bg-[rgba(245,158,11,0.05)]" : "hairline hover:border-white/30"}`}
                                        data-testid={`${testIdPrefix}-place-${p.key}`}>
                                        <div className="flex items-center gap-2">
                                            <span className={`w-3 h-3 border ${on ? "bg-[var(--sb-amber)] border-[var(--sb-amber)]" : "border-white/30"}`} />
                                            <span className="text-sm">{p.label}</span>
                                        </div>
                                        <div className="text-[11px] text-zinc-500 mt-1 ml-5">{p.sub}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Platforms */}
                    <div>
                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-2">Platforms</div>
                        <div className="flex flex-wrap gap-2">
                            {PLATFORMS.map((p) => {
                                const on = (ads.platforms || []).includes(p.key);
                                const Icon = p.Icon;
                                return (
                                    <button key={p.key} type="button" onClick={() => toggleArr("platforms", p.key)}
                                        className={`inline-flex items-center gap-2 border px-3 py-1.5 text-xs transition-colors ${on ? "border-[var(--sb-amber)] text-white bg-[rgba(245,158,11,0.05)]" : "hairline text-zinc-400 hover:border-white/30"}`}
                                        data-testid={`${testIdPrefix}-plat-${p.key}`}>
                                        <Icon size={12} /> {p.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Campaign rule */}
                    <div>
                        <div className="text-[10px] uppercase tracking-ultra-wide text-zinc-500 mb-2">Campaign rule</div>
                        <div className="flex flex-wrap gap-2 mb-3">
                            {RULE_TYPES.map((r) => (
                                <button key={r.key} type="button" onClick={() => set({ rule_type: r.key })}
                                    className={`border px-3 py-1.5 text-xs transition-colors ${ads.rule_type === r.key ? "border-[var(--sb-amber)] bg-[rgba(245,158,11,0.05)] text-white" : "hairline text-zinc-400 hover:border-white/30"}`}
                                    data-testid={`${testIdPrefix}-rule-${r.key}`}>
                                    {r.label}
                                </button>
                            ))}
                        </div>
                        {ads.rule_type === "period" && (
                            <div className="grid grid-cols-2 gap-3">
                                <label className="block">
                                    <span className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Start</span>
                                    <input type="date" value={(ads.period_start || "").slice(0, 10)} onChange={(e) => set({ period_start: e.target.value })}
                                        className="mt-1 w-full bg-transparent border hairline px-3 py-2 text-sm text-white outline-none focus:border-white/30"
                                        data-testid={`${testIdPrefix}-period-start`} />
                                </label>
                                <label className="block">
                                    <span className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">End</span>
                                    <input type="date" value={(ads.period_end || "").slice(0, 10)} onChange={(e) => set({ period_end: e.target.value })}
                                        className="mt-1 w-full bg-transparent border hairline px-3 py-2 text-sm text-white outline-none focus:border-white/30"
                                        data-testid={`${testIdPrefix}-period-end`} />
                                </label>
                            </div>
                        )}
                        {ads.rule_type === "impressions" && (
                            <label className="block">
                                <span className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Impressions cap</span>
                                <input type="number" min="0" value={ads.impressions_cap ?? ""} onChange={(e) => set({ impressions_cap: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                                    placeholder="e.g. 50000"
                                    className="mt-1 w-full bg-transparent border hairline px-3 py-2 text-sm text-white outline-none focus:border-white/30"
                                    data-testid={`${testIdPrefix}-imp-cap`} />
                            </label>
                        )}
                        {ads.rule_type === "clicks" && (
                            <label className="block">
                                <span className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Clicks cap</span>
                                <input type="number" min="0" value={ads.clicks_cap ?? ""} onChange={(e) => set({ clicks_cap: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
                                    placeholder="e.g. 1500"
                                    className="mt-1 w-full bg-transparent border hairline px-3 py-2 text-sm text-white outline-none focus:border-white/30"
                                    data-testid={`${testIdPrefix}-click-cap`} />
                            </label>
                        )}
                    </div>

                    {/* Notes */}
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-ultra-wide text-zinc-500">Internal notes (optional)</span>
                        <textarea rows={2} value={ads.notes || ""} onChange={(e) => set({ notes: e.target.value })}
                            placeholder="e.g. priority slot, swap creative monthly"
                            className="mt-1 w-full bg-transparent border hairline px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-white/30"
                            data-testid={`${testIdPrefix}-notes`} />
                    </label>
                </div>
            )}
        </div>
    );
}
